import { z } from "zod";
import { JsonSchemaFragmentSchema, ScopeSchema } from "./primitives.js";

/**
 * MCP tool annotations as per the current MCP spec (rev 2025-11-25). The spec's defaults
 * are safety-conservative: `destructiveHint` defaults to **true** and `openWorldHint`
 * defaults to **true** when omitted. So omitting = "destructive + open-world" — which is
 * exactly the wrong claim for the majority of tools we generate.
 *
 * Keystone therefore *requires* every tool to make an explicit `readOnlyHint` and
 * `destructiveHint` claim. `idempotentHint` and `openWorldHint` remain optional but the
 * designer should set them whenever known.
 */
export const ToolAnnotationsSchema = z.object({
  readOnlyHint: z.boolean(),
  destructiveHint: z.boolean(),
  idempotentHint: z.boolean().optional(),
  openWorldHint: z.boolean().optional()
});
export type ToolAnnotations = z.infer<typeof ToolAnnotationsSchema>;

export const PaginationSchema = z.object({
  kind: z.enum(["cursor", "page", "offset"]),
  paramNames: z.record(z.string()),
  defaultLimit: z.number().int().positive(),
  maxLimit: z.number().int().positive()
});
export type Pagination = z.infer<typeof PaginationSchema>;

/**
 * Tool name convention: action-oriented `service_action_resource`. Lowercase
 * snake_case with at least two underscores enforcing the convention.
 */
export const ToolNameSchema = z
  .string()
  .regex(/^[a-z][a-z0-9]*(_[a-z0-9]+){1,}$/, "tool name must be service_action[_resource] in snake_case");
export type ToolName = z.infer<typeof ToolNameSchema>;

export const ToolDesignEntrySchema = z.object({
  name: ToolNameSchema,
  title: z.string().min(1),
  description: z.string().min(20, "model-facing description must be substantive"),
  inputSchema: JsonSchemaFragmentSchema,
  outputSchema: JsonSchemaFragmentSchema,
  annotations: ToolAnnotationsSchema,
  pagination: PaginationSchema.optional(),
  /** OperationIds from the CapabilityModel this tool covers. */
  operations: z.array(z.string().min(1)).min(1),
  requiredScopes: z.array(ScopeSchema).min(1)
});
export type ToolDesignEntry = z.infer<typeof ToolDesignEntrySchema>;

export const ToolDesignSchema = z.object({
  schemaVersion: z.literal(1).default(1),
  serverName: z.string().min(1),
  serverVersion: z.string().min(1),
  tools: z.array(ToolDesignEntrySchema).min(1)
});
export type ToolDesign = z.infer<typeof ToolDesignSchema>;
